import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import L from "leaflet";
import { displayErrorNotification } from "../../../../../actions/notifications";
import { downloadXlsx } from "../../../../../utils/spreadsheet";
import { useSpeciesColors } from "../useSpeciesColors";
import { entryLabel } from "../PivotTable/pivotModel";
import { describeDates } from "../Filters/filterState";
import { reportPath } from "../reportUrl";
import { MAX_RADIUS, mapDataRows, mapModel } from "./mapModel";
import { downloadMapPng } from "./mapImage";
import { useSpeciesMap } from "./useSpeciesMap";

const TITLE = "Species map";
// room for the pies' own size
const EDGE = MAX_RADIUS + 8;

// Fitting leaves the sites clear of the panel, on whichever side it sits.
const fitOptions = (map, panel) => {
  let left = EDGE;
  let right = EDGE;
  const box = map.getContainer().getBoundingClientRect();
  const rect = panel?.getBoundingClientRect();
  if (rect?.width) {
    if (rect.left + rect.width / 2 < box.left + box.width / 2) left = rect.right - box.left + EDGE;
    else right = box.right - rect.left + EDGE;
  }
  // a panel too wide for the map to work around: fit the whole map
  if (left + right > box.width - 2 * EDGE) left = right = EDGE;
  return { paddingTopLeft: [left, EDGE], paddingBottomRight: [right, EDGE], maxZoom: 13 };
};
const NOTHING = { pies: [], unplaced: [], legend: [] };

/**
 * The Raw data report's filters for one count on the map: the map's period,
 * the site (or no site), and the species when the count is one species'.
 */
export const countFilters = (filters, { row, column }) => {
  const site = row?.key?.[0];
  return {
    ...filters,
    species: column ? [entryLabel(column)] : [],
    advanced: {
      op: "and",
      clauses: [
        site
          ? { field: "collection_site", operator: "is", value: site }
          : { field: "collection_site", operator: "is_empty", value: null },
      ],
    },
  };
};

/**
 * The species map on the main map, while `enabled` (the Reports section is
 * open): its pies, and the props for its panel. The view is the user's: the
 * map fits the sites when the map turns on, and after that keeps its center
 * and zoom while the period changes, unless no site would be in view.
 */
export const useSpeciesMapLayer = ({ enabled, map, trapSites }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const colors = useSpeciesColors(enabled);
  const { model, filters, loading, error, period, setPeriod } = useSpeciesMap(enabled);

  const { pies, unplaced, legend } = useMemo(
    () => (enabled && model ? mapModel(model, trapSites, colors) : NOTHING),
    [enabled, model, trapSites, colors]
  );

  const panelRef = useRef(null);
  const fitAll = useCallback(() => {
    if (map && pies.length) map.fitBounds(L.latLngBounds(pies.map((p) => p.position)), fitOptions(map, panelRef.current));
  }, [map, pies]);
  const fitted = useRef(false);
  useEffect(() => {
    if (!enabled) {
      fitted.current = false;
      return;
    }
    if (!map || !pies.length) return;
    const view = map.getBounds();
    if (!fitted.current || !pies.some((p) => view.contains(p.position))) {
      fitted.current = true;
      fitAll();
    }
  }, [enabled, map, pies, fitAll]);

  // a count opens the Raw data report on its specimens, where each opens
  const onOpen = useCallback(
    (cell) => history.push(reportPath("raw-data", { filters: countFilters(filters, cell) })),
    [history, filters]
  );

  const legendItems = legend.map((s) => ({ label: s.label, color: s.color }));
  const onDownloadImage = async () => {
    try {
      await downloadMapPng({ map, pies, title: TITLE, subtitle: describeDates(filters), legend: legendItems });
    } catch (err) {
      dispatch(displayErrorNotification(err?.message || "The map could not be downloaded"));
    }
  };
  const onDownloadData = () => downloadXlsx({ title: TITLE, rows: mapDataRows(model, trapSites) });

  return {
    pies,
    onOpen,
    panel: {
      period,
      onPeriod: setPeriod,
      loading,
      error,
      legend,
      unplaced,
      hasPies: pies.length > 0,
      onOpen,
      onFit: fitAll,
      onDownloadImage,
      onDownloadData,
      panelRef,
    },
  };
};
